import { createContext, useContext, ReactNode, useState } from 'react';
import { useHomeIntelligence } from '../hooks/useHomeIntelligence';
import type {
  HomeIntelligenceData,
  HomeIntelligenceError
} from '../../shared/intelligence/types.js';

interface HomeIntelligenceContextType {
  address: string;
  intelligence: HomeIntelligenceData | null;
  deferredIntelligence: HomeIntelligenceData | null;
  isLoading: boolean;
  isTransitioning: boolean;
  error: HomeIntelligenceError | null;
  isStale: boolean;
  refetch: () => Promise<void>;
  lastUpdated: string | null;
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
}

const HomeIntelligenceContext = createContext<HomeIntelligenceContextType | undefined>(undefined);

interface HomeIntelligenceProviderProps {
  children: ReactNode;
  address: string;
  enabled?: boolean;
}

export function HomeIntelligenceProvider({
  children,
  address,
  enabled = true
}: HomeIntelligenceProviderProps) {
  const [manuallyDisabled, setManuallyDisabled] = useState(false);

  // Derive the actual enabled state: must have enabled prop AND not manually disabled
  const actualEnabled = enabled && !manuallyDisabled;

  const intelligence = useHomeIntelligence({
    address,
    enabled: actualEnabled,
  });

  // Provide a setEnabled function that works with the expected interface
  const setEnabled = (newEnabled: boolean) => {
    setManuallyDisabled(!newEnabled);
  };

  return (
    <HomeIntelligenceContext.Provider value={{
      address,
      enabled: actualEnabled,
      setEnabled,
      ...intelligence
    }}>
      {children}
    </HomeIntelligenceContext.Provider>
  );
}

export function useHomeIntelligenceContext() {
  const context = useContext(HomeIntelligenceContext);
  if (context === undefined) {
    throw new Error('useHomeIntelligenceContext must be used within a HomeIntelligenceProvider');
  }
  return context;
}

// Utility hook for components that want to conditionally show intelligence
export function useHomeIntelligenceOptional() {
  return useContext(HomeIntelligenceContext);
}